/**
 * "Create with Mochi" (spec §5.8.6), laid out as the design's Create page
 * (weco2/files-mochi/fm-create.jsx): pick Present / Write / Grid, describe
 * it (attach anything that helps), read Mochi's streamed plan
 * (`POST /live/brief/`), then Create (`POST /live/generate/`, available at any time) creates the
 * file and opens it with `?build=<runId>` so the editor follows Mochi as it
 * builds. The plan, the replies and the file are all real — nothing scripted.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, AudioWaveform, FileSpreadsheet, FileText, Monitor, Paperclip, WandSparkles, type LucideIcon } from "lucide-react";

import { ChatAccessNotice, useChatAccess } from "../../../modules/assistant/components/hub/ChatAccessNotice";
import { driveService } from "../../../modules/vault/api";
import { FMAvatar, FMLauncher, FMMd, FMScene, FMSpin, cx } from "../fm/primitives";
import { liveApi, type LiveChatMessage } from "../live/liveApi";
import type { Attachment } from "./attachments";
import type { PickedFile } from "./FilesPicker";
import { CREATE_KINDS, KIND_COPY, editorPath, type CreateKind } from "./kinds";
import { MochiComposer } from "./MochiComposer";
import { isAbort, plainCitations, streamStudio } from "./studioStream";

const KIND_ICON: Record<CreateKind, LucideIcon> = {
  presentation: Monitor,
  document: FileText,
  spreadsheet: FileSpreadsheet,
};

interface Turn {
  role: "user" | "mochi";
  text: string;
  fileIds?: string[];
  fileNames?: string[];
  /** Said in the voice call. */
  voice?: boolean;
  /** Mochi's reply is still streaming. */
  pending?: boolean;
  activity?: string | null;
  error?: boolean;
}

export interface MochiCreateChatProps {
  kind?: CreateKind;
  onKindChange?: (kind: CreateKind) => void;
  /** Drive item ids to pre-attach (≤ 8). */
  initialFileIds?: string[];
  /** Shown as the back arrow (Hub page). */
  onBack?: () => void;
  /** Shown as the back arrow when the page is a layer over another screen. */
  onClose?: () => void;
  /** Called after the file is created, before navigating. */
  onCreated?: (fileId: string, runId: string, path: string) => void;
  /** Heading id for a dialog's aria-labelledby. */
  headingId?: string;
  /** Show the floating Mochi launcher (default on, as in the design). */
  launcher?: boolean;
  className?: string;
}

const MAX_SENT = 40;
const MAX_TEXT = 8000;

export function MochiCreateChat({
  kind: kindProp,
  onKindChange,
  initialFileIds,
  onBack,
  onClose,
  onCreated,
  headingId,
  launcher = true,
  className,
}: MochiCreateChatProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { access, name } = useChatAccess();
  const [ownKind, setOwnKind] = useState<CreateKind>(kindProp ?? "presentation");
  const kind = kindProp ?? ownKind;
  const [turns, setTurns] = useState<Turn[]>([]);
  const [pendingFiles, setPendingFiles] = useState(0);
  const [exact, setExact] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [making, setMaking] = useState(false);
  const [makeError, setMakeError] = useState<string | null>(null);
  const [initialFiles, setInitialFiles] = useState<PickedFile[] | null>(initialFileIds?.length ? null : []);
  const abortRef = useRef<AbortController | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const blocked = access !== "ok";
  const copy = KIND_COPY[kind];
  const started = turns.length > 0;

  useEffect(() => () => abortRef.current?.abort(), []);

  // "Create from these": fetch the names of pre-attached files.
  useEffect(() => {
    if (!initialFileIds?.length) return;
    let live = true;
    Promise.all(
      initialFileIds.slice(0, 8).map((id) =>
        driveService
          .getItem(id)
          .then((it) => ({ id: it.id, name: it.name, size: it.size }))
          .catch(() => null),
      ),
    ).then((found) => {
      if (live) setInitialFiles(found.filter((f): f is PickedFile => !!f));
    });
    return () => {
      live = false;
    };
  }, [initialFileIds]);

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  const chooseKind = (next: CreateKind) => {
    if (kindProp === undefined) setOwnKind(next);
    onKindChange?.(next);
  };

  const wire = (list: Turn[]): LiveChatMessage[] =>
    list
      .filter((m) => !m.pending && !m.error && (m.text.trim() || m.fileIds?.length))
      .slice(-MAX_SENT)
      .map((m) => ({
        role: m.role,
        text: m.text.slice(0, MAX_TEXT),
        ...(m.fileIds?.length ? { file_ids: m.fileIds } : {}),
      }));

  const attachedAny = turns.some((m) => m.fileIds?.length) || pendingFiles > 0;

  /** One turn: the person's words (and files) → Mochi's streamed plan. Resolves with the reply. */
  const send = useCallback(
    async (text: string, attachments: Attachment[], opts: { voice?: boolean } = {}): Promise<string | null> => {
      const fileIds = attachments.map((a) => a.fileId).filter((id): id is string => !!id);
      const userTurn: Turn = {
        role: "user",
        text:
          text ||
          t("mochi.create.attachedOnly", { defaultValue: "(attached {{count}} files)", count: fileIds.length }),
        fileIds,
        fileNames: attachments.map((a) => a.name),
        voice: opts.voice,
      };
      const history = [...turns.filter((m) => !m.error), userTurn];
      setTurns([...history, { role: "mochi", text: "", pending: true, activity: null, voice: opts.voice }]);
      setStreaming(true);
      setMakeError(null);
      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;
      const update = (change: Partial<Turn>) =>
        setTurns((list) => {
          const next = [...list];
          const last = next[next.length - 1];
          if (last?.role === "mochi") next[next.length - 1] = { ...last, ...change };
          return next;
        });
      let reply = "";
      let failure: string | null = null;
      try {
        await streamStudio(
          "/live/brief/",
          { kind, messages: wire(history), exact: history.some((m) => m.fileIds?.length) && exact },
          (e) => {
            if (e.type === "activity") update({ activity: e.label });
            else if (e.type === "delta") {
              reply += e.text;
              update({ text: reply, activity: null });
            } else if (e.type === "done") {
              reply = e.text || reply;
              update({ text: reply, pending: false, activity: null });
            } else if (e.type === "error") {
              failure = e.message || "error";
              update({ text: e.message, pending: false, error: true });
            }
          },
          controller.signal,
        );
        if (failure) {
          setStreaming(false);
          throw new Error(failure);
        }
        update({ pending: false });
        return reply ? plainCitations(reply) : null;
      } catch (err) {
        if (isAbort(err)) return null;
        if (failure) throw err;
        const message =
          (err as Error)?.message ||
          t("mochi.create.unreachable", { defaultValue: "{{name}} couldn’t be reached. Try again?", name });
        update({ text: message, pending: false, error: true });
        throw new Error(message);
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setStreaming(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [turns, kind, exact, name, t],
  );
  const sendRef = useRef(send);
  sendRef.current = send;

  const ask = (text: string, attachments: Attachment[] = []) => {
    if (blocked || making) return;
    sendRef.current(text, attachments).catch(() => {
      /* shown in the thread */
    });
  };

  const userTurns = turns.filter((m) => m.role === "user");
  const canMake = !blocked && !making && userTurns.length > 0;

  const makeIt = async () => {
    if (!canMake) return;
    // Pressed mid-reply: keep only what is complete, stop the stream.
    const messages = wire(turns);
    abortRef.current?.abort();
    setTurns((list) => list.filter((m) => !m.pending));
    setMaking(true);
    setMakeError(null);
    try {
      const { file_id, run } = await liveApi.generate({
        kind,
        messages,
        exact: attachedAny && exact,
      });
      const path = editorPath(kind, file_id, run?.id);
      onCreated?.(file_id, run?.id, path);
      navigate(path);
    } catch (err) {
      setMakeError(
        (err as Error)?.message ||
          t("mochi.create.makeFailed", "That didn’t start — try again in a moment."),
      );
      setMaking(false);
    }
  };

  const heading = t("mochi.create.title", { defaultValue: "Create with {{name}}", name });
  const hId = headingId ?? "mochi-create-h";
  const focusComposer = () => inputRef.current?.focus();

  // Create pressed: the design's loading screen while the file is created.
  if (making) {
    return (
      <section
        className={cx("fm fm-bg flex min-h-0 flex-1 flex-col items-center", className)}
        aria-labelledby={hId}
        role="status"
      >
        <h2 id={hId} className="sr-only">
          {heading}
        </h2>
        {launcher && <FMLauncher working open onClick={() => {}} />}
        <div className="mt-10 flex items-center gap-2 text-[15px] text-[color:var(--fm-grey)]">
          <FMSpin size={15} />
          {t("filesMochi.create.loading", "Loading")}
        </div>
      </section>
    );
  }

  const back = onBack ?? onClose;
  return (
    <section className={cx("fm fm-bg flex min-h-0 flex-1 flex-col", className)} aria-labelledby={hId}>
      {launcher && (
        <FMLauncher
          working={streaming}
          open
          onClick={focusComposer}
          label={t("filesMochi.create.focusComposer", { defaultValue: "Focus the {{name}} composer", name })}
        />
      )}
      <div className="mx-auto flex w-full max-w-[800px] shrink-0 items-center gap-3 px-4 pt-4 sm:px-6">
        {back && (
          <button
            type="button"
            onClick={back}
            className="fm-ibtn"
            aria-label={onBack ? t("mochi.create.back", "Back") : t("mochi.create.close", "Close")}
          >
            <ArrowLeft size={18} className="rtl:rotate-180" aria-hidden />
          </button>
        )}
        <h1 id={hId} className="text-[20px] font-semibold tracking-tight">
          {heading}
        </h1>
      </div>
      <div className="flex shrink-0 justify-center pt-3">
        <div className="fm-seg" role="tablist" aria-label={t("mochi.create.kindLabel", "What to make")}>
          {CREATE_KINDS.map((k) => {
            const Icon = KIND_ICON[k];
            return (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={k === kind}
                disabled={streaming}
                onClick={() => chooseKind(k)}
              >
                <Icon size={16} aria-hidden />
                {t(KIND_COPY[k].appKey, KIND_COPY[k].app)}
              </button>
            );
          })}
        </div>
      </div>

      <div ref={logRef} className="min-h-0 flex-1 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
        <div className="mx-auto w-full max-w-[740px] px-4 py-6 sm:px-6">
          <ChatAccessNotice access={access} name={name} />
          {!started ? (
            <div className="fm-fade flex flex-col items-center text-center">
              <FMScene size={104} />
              <h2 className="mt-4 text-[28px] font-semibold tracking-tight">
                {t("mochi.create.emptyTitle", "What shall we make?")}
              </h2>
              <p
                className="mt-2 max-w-[470px] text-[15px] leading-relaxed text-[color:var(--fm-grey)]"
                style={{ textWrap: "pretty" } as CSSProperties}
              >
                {t(
                  "mochi.create.emptyHint",
                  "Tell me about it in your own words, and attach anything that helps — photos, a logo, a brand guide, an older deck, notes. I’ll match the look.",
                )}
              </p>
              <ul
                className="mt-5 flex max-w-[560px] flex-wrap justify-center gap-2.5"
                aria-label={t("mochi.create.examples", "Examples")}
              >
                {copy.examples.map((ex) => {
                  const label = ex.key ? t(ex.key, ex.text) : ex.text;
                  return (
                    <li key={ex.text}>
                      <button
                        type="button"
                        className="fm-chip disabled:opacity-50"
                        style={{ height: 34, fontSize: 14, padding: "0 14px" }}
                        dir="auto"
                        disabled={blocked || initialFiles === null}
                        onClick={() => ask(label)}
                      >
                        {label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <div className="fm-create-thread" style={{ fontSize: 15 }}>
              <ol className="fm-thread" aria-live="polite" role="log">
                {turns.map((m, i) =>
                  m.role === "user" ? (
                    <li key={i} className="fm-u" dir="auto">
                      {m.voice && <AudioWaveform size={12} className="me-1.5 -mt-0.5 inline opacity-80" aria-hidden />}
                      {m.text}
                      {!!m.fileNames?.length && (
                        <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[12.5px] opacity-85">
                          <Paperclip size={12} aria-hidden />
                          {m.fileNames.join(" · ")}
                        </span>
                      )}
                    </li>
                  ) : (
                    <li key={i} className="fm-m">
                      <FMAvatar size={22} />
                      <div className="min-w-0">
                        {m.pending && !m.text ? (
                          <span className="fm-status-solo">
                            <FMSpin size={12} />
                            {m.activity ? `${m.activity}…` : t("mochi.create.reading", "Reading…")}
                          </span>
                        ) : m.error ? (
                          <div className="fm-m-card is-err" role="alert" dir="auto">
                            {m.text}
                          </div>
                        ) : (
                          <div className="fm-m-card" data-testid="mochi-brief">
                            <FMMd text={plainCitations(m.text)} />
                            {m.pending && (
                              <div className="fm-status">
                                <FMSpin />
                                {m.activity ? `${m.activity}…` : t("filesMochi.thread.writing", "Writing…")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  ),
                )}
              </ol>
            </div>
          )}
        </div>
      </div>

      <div className="fm-create-comp mx-auto w-full max-w-[800px] shrink-0 px-4 pb-5 sm:px-6">
        {attachedAny && (
          <div className="flex justify-center pb-3">
            <div className="fm-choice" role="radiogroup" aria-label={t("mochi.create.exactLabel", "How to use your files")}>
              {[
                { value: true, label: t("mochi.create.exact", "Build it exactly like my file") },
                { value: false, label: t("mochi.create.reference", "Use my files as a reference") },
              ].map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  role="radio"
                  aria-checked={exact === opt.value}
                  onClick={() => setExact(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {started && (
          <div className="flex flex-col items-center gap-2 pb-3">
            <button
              type="button"
              onClick={() => void makeIt()}
              disabled={!canMake}
              className="fm-pill fm-pill-blue"
              style={{ height: 40, padding: "0 20px", fontSize: 15 }}
            >
              <WandSparkles size={16} aria-hidden />
              {t("mochi.create.make", "Create")}
            </button>
            {makeError && (
              <p role="alert" className="fm-err text-[13px]">
                {makeError}
              </p>
            )}
          </div>
        )}
        {initialFiles === null ? (
          <div className="flex items-center justify-center py-4 text-[color:var(--fm-grey)]">
            <FMSpin size={16} />
          </div>
        ) : (
          <MochiComposer
            size="lg"
            autoFocus
            inputRef={inputRef}
            initialFiles={started ? undefined : initialFiles}
            onSend={(text, files) => ask(text, files)}
            onAttachmentsChange={(list) => setPendingFiles(list.length)}
            busy={streaming}
            disabled={blocked}
            placeholder={
              started
                ? t("filesMochi.create.replyPlaceholder", { defaultValue: "Reply to {{name}}, or add more…", name })
                : t("filesMochi.create.placeholder", {
                    defaultValue: "Describe the {{noun}} you want…",
                    noun: t(copy.nounKey, copy.noun),
                  })
            }
            hint={
              started
                ? null
                : t("filesMochi.create.hint", { defaultValue: "Attach photos or files — {{name}} reads their look too", name })
            }
            voice={{
              where: heading,
              onTurn: (text) => sendRef.current(text, [], { voice: true }),
            }}
          />
        )}
      </div>
    </section>
  );
}
